import { bpHistory } from 'global/bpHistory';
import { TabId } from 'store/modules/full-employee-form/types';
import { EmployeeActionObject } from 'store/modules/add-employees/actionRules';
import { generatePath } from 'react-router-dom';
import { ROUTES_CONFIG_V2, RouteV2Id } from 'routes/routesConfigV2';
import { SubChecklistCard, Subscriber } from 'types/electionService';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { employerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';

function quoteEmployeeFormQuery(context: {
  groupId: string;
  periodId: string;
  subscriberId: string;
  cartId?: string;
  activeTabId?: TabId;
}): string {
  const { groupId, periodId, cartId, subscriberId, activeTabId } = context;
  const params = new URLSearchParams({ subscriberId });
  if (activeTabId) {
    params.set('activeTabId', activeTabId);
  }
  return `${generatePath(ROUTES_CONFIG_V2[RouteV2Id.IMQ_ADD_EMPLOYEES_FULL_FORM].path, {
    groupId,
    periodId,
    cartId,
  })}?${params.toString()}`;
}

function getChecklistEntityId(employee: Subscriber, card: SubChecklistCard, cartId?: string): string {
  const taskEntityId = employee.tasks?.find((task) =>
    task.checklist?.checklistOptions?.some((option) => option.card === card),
  )?.entityId;

  return taskEntityId || cartId || '';
}

export enum IMQAddEmployeesEmployeeAction {
  IMQ_ADD_EMPLOYEES_EMPLOYEE_PROFILE = 'IMQ_ADD_EMPLOYEES_EMPLOYEE_PROFILE',
  IMQ_ADD_EMPLOYEES_MANAGE_DEPENDENTS = 'IMQ_ADD_EMPLOYEES_MANAGE_DEPENDENTS',
  IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE = 'IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE',
  IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS = 'IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS',
}

export const imqAddEmployeesEmployeeActions: EmployeeActionObject[] = [
  {
    type: IMQAddEmployeesEmployeeAction.IMQ_ADD_EMPLOYEES_EMPLOYEE_PROFILE,
    name: 'Employee Profile',
    handler: (context): void => {
      bpHistory.push(
        quoteEmployeeFormQuery({
          groupId: context.static.groupId,
          periodId: context.static.periodId,
          cartId: context.static.cartId,
          subscriberId: context.static.employeeData.id,
        }),
      );
    },
  },
  {
    type: IMQAddEmployeesEmployeeAction.IMQ_ADD_EMPLOYEES_MANAGE_DEPENDENTS,
    name: 'Manage Dependents',
    handler: (context): void => {
      bpHistory.push(
        quoteEmployeeFormQuery({
          groupId: context.static.groupId,
          periodId: context.static.periodId,
          cartId: context.static.cartId,
          subscriberId: context.static.employeeData.id,
          activeTabId: TabId.DEPENDENTS,
        }),
      );
    },
  },
  {
    type: IMQAddEmployeesEmployeeAction.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE,
    name: 'Health Questionnaire',
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => !employerRights(userRoles),
    handler: (context): void => {
      const { groupId, periodId, cartId, employeeData } = context.static;
      const entityId = getChecklistEntityId(employeeData, SubChecklistCard.HEALTH_QUESTIONNAIRE, cartId);

      bpHistory.push(
        generatePath(ROUTES_CONFIG_V2[RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_ENTRY].path, {
          groupId,
          periodId,
          cartId,
          subscriberId: employeeData.id,
          entityId: entityId || '',
        }),
      );
    },
  },
  {
    type: IMQAddEmployeesEmployeeAction.IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS,
    name: 'Employment Details',
    handler: (context): void => {
      const { groupId, periodId, cartId, employeeData } = context.static;
      const entityId = getChecklistEntityId(employeeData, SubChecklistCard.IMQ_GENERAL_INFO, cartId);

      bpHistory.push(
        generatePath(ROUTES_CONFIG_V2[RouteV2Id.IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS].path, {
          groupId,
          periodId,
          cartId,
          subscriberId: employeeData.id,
          entityId,
        }),
      );
    },
  },
];
